/**
 * Utility to imperatively set <head> meta tags for SPA pages.
 * Used for Open Graph / Twitter cards so social shares render correctly.
 *
 * usage:
 *   useSocialMeta({
 *     title, description, image, url, type: 'article',
 *     siteName: 'Skopelos Cottages',
 *   })
 *
 * Returns a cleanup function so previous values are restored on unmount.
 */
import { useEffect } from 'react';

const TAGS = [
  // [attribute, key, source-key]
  ['property', 'og:title', 'title'],
  ['property', 'og:description', 'description'],
  ['property', 'og:image', 'image'],
  ['property', 'og:image:width', 'imageWidth'],
  ['property', 'og:image:height', 'imageHeight'],
  ['property', 'og:url', 'url'],
  ['property', 'og:type', 'type'],
  ['property', 'og:site_name', 'siteName'],
  ['name', 'twitter:card', 'card'],
  ['name', 'twitter:title', 'title'],
  ['name', 'twitter:description', 'description'],
  ['name', 'twitter:image', 'image'],
  ['name', 'description', 'description'],
];

function findOrCreate(attr, key) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

function findOrCreateLink(rel) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

export function useSocialMeta(opts) {
  useEffect(() => {
    if (!opts) return;
    const merged = {
      type: 'website',
      card: 'summary_large_image',
      siteName: 'Skopelos Cottages',
      ...opts,
    };

    // Title
    const prevTitle = document.title;
    if (merged.title) document.title = merged.title;

    // Meta tags
    const previous = [];
    TAGS.forEach(([attr, key, srcKey]) => {
      const value = merged[srcKey];
      if (!value) return;
      const { el, created } = findOrCreate(attr, key);
      previous.push({ el, attr, key, created, prev: el.getAttribute('content') });
      el.setAttribute('content', String(value));
    });

    // Canonical
    let canonicalPrev = null;
    let canonicalCreated = false;
    let canonicalEl = null;
    if (merged.url) {
      const { el, created } = findOrCreateLink('canonical');
      canonicalEl = el;
      canonicalCreated = created;
      canonicalPrev = el.getAttribute('href');
      el.setAttribute('href', merged.url);
    }

    return () => {
      document.title = prevTitle;
      previous.forEach(({ el, created, prev }) => {
        if (created && el.parentNode) {
          el.parentNode.removeChild(el);
        } else if (prev !== null) {
          el.setAttribute('content', prev);
        }
      });
      if (canonicalEl) {
        if (canonicalCreated && canonicalEl.parentNode) {
          canonicalEl.parentNode.removeChild(canonicalEl);
        } else if (canonicalPrev !== null) {
          canonicalEl.setAttribute('href', canonicalPrev);
        }
      }
    };
  }, [
    opts?.title,
    opts?.description,
    opts?.image,
    opts?.url,
    opts?.type,
    opts?.card,
    opts?.siteName,
  ]);
}
